/**
 * Типы указателей режима «Навигация»: разбор справочника из настроек карты и отрисовка
 * указателей по типу (docs/wayfinding/plan.md §9.1.1).
 *
 * Справочник живёт в `project.settings.navigation.signTypes`, а не отдельной таблицей:
 * он маленький, правится целиком и при дублировании карты уезжает вместе с ней даром.
 */
import type {
  CustomPropertyDef,
  LayerStyleConfig,
  LayerVisualization,
  VisualizationKind,
} from '../types/layer.js';
import { AUTOCRAFT_MAX_PREVIEW_PAGE } from '../types/autocraft.js';
import {
  WAYFINDING_LAYER_SIGN_TYPE,
  WAYFINDING_SIGN_MAX_SIDES,
  WAYFINDING_SIGN_LABEL_ANCHOR_PROPERTY,
  WAYFINDING_SIGN_NAME_LABEL_PROPERTY,
  WAYFINDING_SIGN_ORIENTATION_PROPERTY,
  WAYFINDING_SIGN_PREVIEWS_PROPERTY,
  WAYFINDING_SIGN_PREVIEW_ANCHOR_BY_PAGE_PROPERTY,
  WAYFINDING_SIGN_PREVIEW_ANCHOR_PROPERTY,
  WAYFINDING_SIGN_TYPE_PROPERTY,
  WAYFINDING_SIGN_UNTYPED_HEX,
  wayfindingSignShape,
  type WayfindingMapSize,
  type WayfindingSignShape,
  type WayfindingSignType,
} from '../types/wayfinding.js';
import { withLabelLayoutDefaults } from './layerLabelOwner.js';
import { wayfindingSignDefaultPreviewAnchor } from './wayfindingSignPreviewDirections.js';
import {
  orderVisualizationsForRender,
  resolveVisualizations,
  visualizationKindGeometry,
  type GeometryCategory,
} from './resolveVisualizations.js';

/** Цвета новых типов по кругу — чтобы соседние типы на плане не сливались. */
export const WAYFINDING_SIGN_TYPE_COLORS: readonly string[] = [
  '#6366f1',
  '#f97316',
  '#10b981',
  '#0ea5e9',
  '#e11d48',
  '#eab308',
  '#8b5cf6',
  '#14b8a6',
];

/** Стабильная пустышка: новый массив на каждом чтении ломал бы мемоизацию потребителей. */
const EMPTY_SIGN_TYPES: WayfindingSignType[] = [];

/**
 * Только hex. Цвет уходит литералом в `match`-выражение MapLibre, а невалидный литерал
 * там роняет весь слой при `addLayer` — указатели пропали бы с карты целиком.
 */
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/**
 * Справочник типов из свободного jsonb настроек карты.
 *
 * `settings` пишут несколько ручек, и там законно лежит что угодно: объект вместо
 * массива, строки вместо записей, `null` среди элементов. Голый `as` уронил бы правую
 * панель на первом `.map` — и не там, где ошибка появилась. Дубли `id` отбрасываем:
 * правка и удаление задевали бы сразу обе записи.
 */
export function readWayfindingSignTypes(settings: unknown): WayfindingSignType[] {
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
    return EMPTY_SIGN_TYPES;
  }
  const navigation = (settings as Record<string, unknown>).navigation;
  if (!navigation || typeof navigation !== 'object' || Array.isArray(navigation)) {
    return EMPTY_SIGN_TYPES;
  }
  const raw = (navigation as Record<string, unknown>).signTypes;
  if (!Array.isArray(raw)) {
    return EMPTY_SIGN_TYPES;
  }
  const seen = new Set<string>();
  const parsed = raw.flatMap((item): WayfindingSignType[] => {
    if (!item || typeof item !== 'object') {
      return [];
    }
    const entry = item as Record<string, unknown>;
    if (typeof entry.id !== 'string' || entry.id === '' || typeof entry.name !== 'string') {
      return [];
    }
    if (seen.has(entry.id)) {
      return [];
    }
    seen.add(entry.id);
    const sides =
      typeof entry.sides === 'number' && Number.isFinite(entry.sides)
        ? Math.max(1, Math.min(WAYFINDING_SIGN_MAX_SIDES, Math.round(entry.sides)))
        : 1;
    const code = readOptionalString(entry.code);
    const icon = readOptionalString(entry.icon);
    const material = readOptionalString(entry.material);
    const generator = readOptionalString(entry.autocraftGeneratorHash);
    const previewAnchor =
      typeof entry.previewAnchor === 'string' && ICON_ANCHORS.has(entry.previewAnchor)
        ? entry.previewAnchor
        : undefined;
    const previewAnchorByPage = readAnchorByPage(entry.previewAnchorByPage);
    const labelAnchor = readIconAnchor(entry.labelAnchor) ?? undefined;
    const pageBySide = readPageBySide(entry.autocraftPageBySide);
    const fieldLinks = readFieldLinks(entry.autocraftFieldLinks);
    return [
      {
        id: entry.id,
        name: entry.name,
        sides,
        color:
          typeof entry.color === 'string' && HEX_COLOR.test(entry.color)
            ? entry.color
            : WAYFINDING_SIGN_TYPE_COLORS[0]!,
        ...(code !== undefined ? { code } : {}),
        ...(icon !== undefined ? { icon } : {}),
        ...(typeof entry.powered === 'boolean' ? { powered: entry.powered } : {}),
        ...(material !== undefined ? { material } : {}),
        ...(generator !== undefined ? { autocraftGeneratorHash: generator } : {}),
        ...(fieldLinks !== undefined ? { autocraftFieldLinks: fieldLinks } : {}),
        ...(previewAnchor !== undefined ? { previewAnchor } : {}),
        ...(previewAnchorByPage !== undefined ? { previewAnchorByPage } : {}),
        ...(labelAnchor !== undefined ? { labelAnchor } : {}),
        ...(pageBySide !== undefined ? { autocraftPageBySide: pageBySide } : {}),
      },
    ];
  });
  return parsed.length > 0 ? parsed : EMPTY_SIGN_TYPES;
}

/**
 * Якоря страниц макета из свободного jsonb: мусор и незнакомые значения отбрасываем,
 * пустая запись — как её отсутствие.
 */
function readAnchorByPage(raw: unknown): Record<string, string> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }
  const out: Record<string, string> = {};
  for (const [page, anchor] of Object.entries(raw as Record<string, unknown>)) {
    const pageNumber = Number(page);
    if (!Number.isInteger(pageNumber) || pageNumber < 1) {
      continue;
    }
    if (typeof anchor === 'string' && ICON_ANCHORS.has(anchor)) {
      out[String(pageNumber)] = anchor;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Страницы макета по сторонам из свободного jsonb: сторона — 1…`WAYFINDING_SIGN_MAX_SIDES`,
 * страница — целое 1…`AUTOCRAFT_MAX_PREVIEW_PAGE`. Мусор отбрасываем, пустая запись — как
 * её отсутствие.
 */
function readPageBySide(raw: unknown): Record<string, number> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }
  const out: Record<string, number> = {};
  for (const [side, page] of Object.entries(raw as Record<string, unknown>)) {
    const sideNumber = Number(side);
    if (!Number.isInteger(sideNumber) || sideNumber < 1 || sideNumber > WAYFINDING_SIGN_MAX_SIDES) {
      continue;
    }
    if (
      typeof page === 'number' &&
      Number.isInteger(page) &&
      page >= 1 &&
      page <= AUTOCRAFT_MAX_PREVIEW_PAGE
    ) {
      out[String(sideNumber)] = page;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Связи полей генератора с полями объектов из свободного jsonb: ключ — ключ поля
 * генератора, значение — имя поля объекта. Мусор и пустые строки отбрасываем, пустая
 * запись — как её отсутствие: тип без связей не должен отличаться от типа, где их сняли.
 */
function readFieldLinks(raw: unknown): Record<string, string> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }
  const out: Record<string, string> = {};
  for (const [field, column] of Object.entries(raw as Record<string, unknown>)) {
    if (field === '' || typeof column !== 'string' || column === '') {
      continue;
    }
    out[field] = column;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Свой якорь превью у слоя указателей поверх якоря типа: все указатели слоя встают превью туда,
 * куда показывает он, а не туда, куда их типы. Ключ в корне `styleConfig`, как и размер знака
 * (`WAYFINDING_LAYER_MARKER_SIZE_KEY`): блоки слоя указателей раскрывает правило чтения, а якорь
 * у слоя один. Нет ключа — якорь типа. Старый корневой `previewAnchor` — про точки Автокрафта в
 * «Редакторе», указателей не касается.
 */
export const WAYFINDING_LAYER_PREVIEW_ANCHOR_KEY = 'wayfindingPreviewAnchor';

/** Якорь из свободного jsonb: только значение, которое примет MapLibre `icon-anchor`. */
function readIconAnchor(value: unknown): string | null {
  return typeof value === 'string' && ICON_ANCHORS.has(value) ? value : null;
}

/** Свой якорь превью слоя указателей; `null` — не задан или значение не годится. */
export function readWayfindingLayerPreviewAnchor(
  styleConfig: Record<string, unknown> | null | undefined,
): string | null {
  return readIconAnchor(styleConfig?.[WAYFINDING_LAYER_PREVIEW_ANCHOR_KEY]);
}

/** Свой якорь превью указателя — переопределение слоя и типа; `null` — не задан. */
export function readWayfindingSignPreviewAnchor(
  properties: Record<string, unknown> | null | undefined,
): string | null {
  return readIconAnchor(properties?.[WAYFINDING_SIGN_PREVIEW_ANCHOR_PROPERTY]);
}

/**
 * Свои якоря страниц макета у слоя указателей поверх якорей страниц типа — рядом с общим якорем
 * слоя (`WAYFINDING_LAYER_PREVIEW_ANCHOR_KEY`): ключ — номер страницы строкой, значение —
 * `icon-anchor`. Страница без записи берёт якорь страницы типа.
 */
export const WAYFINDING_LAYER_PREVIEW_ANCHOR_BY_PAGE_KEY = 'wayfindingPreviewAnchorByPage';

/** Свои якоря страниц слоя указателей; `null` — ни одной годной записи. */
export function readWayfindingLayerPreviewAnchorByPage(
  styleConfig: Record<string, unknown> | null | undefined,
): Record<string, string> | null {
  return readAnchorByPage(styleConfig?.[WAYFINDING_LAYER_PREVIEW_ANCHOR_BY_PAGE_KEY]) ?? null;
}

/** Свои якоря страниц указателя — переопределение слоя и типа; `null` — ни одной годной записи. */
export function readWayfindingSignPreviewAnchorByPage(
  properties: Record<string, unknown> | null | undefined,
): Record<string, string> | null {
  return readAnchorByPage(properties?.[WAYFINDING_SIGN_PREVIEW_ANCHOR_BY_PAGE_PROPERTY]) ?? null;
}

/**
 * Якоря страниц после правки одной: `null` снимает свой якорь страницы. Пустая запись —
 * `undefined`: ключ уходит из настроек целиком, а не остаётся `{}`.
 */
export function withWayfindingPreviewPageAnchor(
  byPage: Readonly<Record<string, string>> | null | undefined,
  page: number,
  anchor: string | null,
): Record<string, string> | undefined {
  const next: Record<string, string> = { ...byPage };
  if (anchor === null) {
    delete next[String(page)];
  } else {
    next[String(page)] = anchor;
  }
  return Object.keys(next).length > 0 ? next : undefined;
}

/**
 * Якорь превью, который действует без своего у указателя: свой у слоя, иначе якорь типа,
 * иначе умолчание по числу сторон. Им панель подсказывает, что будет, если свой снять.
 */
export function resolveWayfindingInheritedPreviewAnchor(
  layerAnchor: string | null | undefined,
  type: Pick<WayfindingSignType, 'sides' | 'previewAnchor'> | null | undefined,
): string | null {
  if (layerAnchor) {
    return layerAnchor;
  }
  return type ? (type.previewAnchor ?? wayfindingSignDefaultPreviewAnchor(type.sides)) : null;
}

/**
 * Якорь подписи указателя по умолчанию — `text-anchor`: подпись под знаком, как было до
 * своего якоря. Не зависит от числа сторон: подпись у знака одна, а не по стороне.
 */
export const WAYFINDING_SIGN_DEFAULT_LABEL_ANCHOR = 'top';

/**
 * Свой якорь подписи у слоя указателей поверх якоря подписи типа — рядом с якорем превью
 * (`WAYFINDING_LAYER_PREVIEW_ANCHOR_KEY`), ключ в корне `styleConfig` по той же причине. Нет
 * ключа — якорь типа. Штатный `text-anchor` стиля подписи у слоя указателей не действует:
 * место подписи у знака задаёт эта цепочка (`applySignTypeStyle`).
 */
export const WAYFINDING_LAYER_LABEL_ANCHOR_KEY = 'wayfindingLabelAnchor';

/** Свой якорь подписи слоя указателей; `null` — не задан или значение не годится. */
export function readWayfindingLayerLabelAnchor(
  styleConfig: Record<string, unknown> | null | undefined,
): string | null {
  return readIconAnchor(styleConfig?.[WAYFINDING_LAYER_LABEL_ANCHOR_KEY]);
}

/** Свой якорь подписи указателя — переопределение слоя и типа; `null` — не задан. */
export function readWayfindingSignLabelAnchor(
  properties: Record<string, unknown> | null | undefined,
): string | null {
  return readIconAnchor(properties?.[WAYFINDING_SIGN_LABEL_ANCHOR_PROPERTY]);
}

/** Якорь подписи без своего у указателя: свой у слоя, иначе якорь типа, иначе умолчание. */
export function resolveWayfindingInheritedLabelAnchor(
  layerAnchor: string | null | undefined,
  type: Pick<WayfindingSignType, 'labelAnchor'> | null | undefined,
): string {
  return layerAnchor || type?.labelAnchor || WAYFINDING_SIGN_DEFAULT_LABEL_ANCHOR;
}

/** Свой тип указателя — переопределение типа слоя; `null` — не задан. */
export function readWayfindingSignTypeId(
  properties: Record<string, unknown> | null | undefined,
): string | null {
  const value = properties?.[WAYFINDING_SIGN_TYPE_PROPERTY];
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * Тип носителя слоя указателей; `null` — у слоя типа нет, тип у каждого указателя свой.
 * `unknown`: у вьювера `sourceConfig` — типизированный интерфейс без индексной сигнатуры.
 */
export function readWayfindingLayerSignTypeId(sourceConfig: unknown): string | null {
  if (!sourceConfig || typeof sourceConfig !== 'object' || Array.isArray(sourceConfig)) {
    return null;
  }
  const value = (sourceConfig as Record<string, unknown>)[WAYFINDING_LAYER_SIGN_TYPE];
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * Тип указателя с учётом слоя (✏️ 13.09): свой тип точки — переопределение, иначе тип слоя.
 *
 * `known` — ключи справочника. С ним ответ — только тип из справочника. Свой тип, которого
 * там нет (точка с другой карты, импорт), тип слоя не заслоняет: так же рисует карта —
 * незнакомое значение уходит в фолбэк маркера, а фолбэк у слоя с типом — знак этого типа.
 * Удалённый тип слоя — «без типа», как у фолбэка карты и селекта слоя. Без `known` — просто
 * «свой, иначе слоя»: так считает область нумерации, и так же её считает SQL на сервере
 * (`bucketSql`), которому справочник не виден.
 */
export function resolveWayfindingSignTypeId(
  properties: Record<string, unknown> | null | undefined,
  layerSignTypeId: string | null | undefined,
  known?: { has(id: string): boolean },
): string | null {
  const own = readWayfindingSignTypeId(properties);
  if (own && (!known || known.has(own))) {
    return own;
  }
  if (layerSignTypeId && known && !known.has(layerSignTypeId)) {
    return null;
  }
  return layerSignTypeId ?? null;
}

/**
 * Геометрия фигур маркера — SVG-путь в квадрате 64×64 с центром в (32, 32). Одна на всех:
 * её рисуют холст иконки карты, SVG в панелях и `pdf-lib` в выгрузке — иначе знак на экране
 * и на напечатанном плане разошлись бы. Поля оставлены под белую обводку.
 */
export const WAYFINDING_SIGN_SHAPE_BOX = 64;
export const WAYFINDING_SIGN_SHAPE_PATHS: Readonly<Record<WayfindingSignShape, string>> = {
  circle: 'M 5 32 A 27 27 0 1 0 59 32 A 27 27 0 1 0 5 32 Z',
  // Односторонний носитель — плоская панель, чуть тоньше 2 : 1 (✏️ 13.09): так он легче
  // отличается от квадрата четырёхстороннего и от пары панелей двустороннего.
  rect: 'M 4 20 H 60 V 44 H 4 Z',
  // Двусторонний — две такие панели друг над другом: читается как два односторонних спиной
  // к спине. Зазор узкий (3 из 64, ✏️ 13.09: широкий читался как два разных знака); белую
  // обводку холст и SVG кладут по общему контуру (`WAYFINDING_SIGN_SHAPE_OUTLINES`), а не по
  // каждой панели — иначе обводка внутрь раздувала бы зазор втрое.
  'rect-double': 'M 4 13 H 60 V 30.5 H 4 Z M 4 33.5 H 60 V 51 H 4 Z',
  triangle: 'M 32 6 L 59 54 H 5 Z',
  square: 'M 9 9 H 55 V 55 H 9 Z',
};
/** Радиус круга в той же сетке — масштаб для «маркер радиусом r». */
export const WAYFINDING_SIGN_SHAPE_RADIUS = 27;

/**
 * Белая обводка фигуры в единицах той же сетки: отделяет знак от плана под ним. Одна на карту и
 * на лист выгрузки (✏️ 20.10.26: в PDF её не было — знак сливался с чертежом).
 */
export const WAYFINDING_SIGN_SHAPE_STROKE = 5;

/**
 * Внешний контур составной фигуры — по нему кладётся фон под зазоры и белая обводка. Фигуре
 * из одного контура своя запись не нужна: контур — сам путь (`wayfindingSignShapeOutline`).
 */
export const WAYFINDING_SIGN_SHAPE_OUTLINES: Readonly<Partial<Record<WayfindingSignShape, string>>> = {
  'rect-double': 'M 4 13 H 60 V 51 H 4 Z',
};

export function wayfindingSignShapeOutline(shape: WayfindingSignShape): string {
  return WAYFINDING_SIGN_SHAPE_OUTLINES[shape] ?? WAYFINDING_SIGN_SHAPE_PATHS[shape];
}

/**
 * Иконки маркеров указателей рисует клиент по имени (резолвер недостающих иконок карты,
 * `signMarkerIcons.ts` в вебе): всё нужное для картинки зашито в сам id. Так фигура
 * перекрашивается правкой цвета типа без перерегистрации картинок, и вьювер рисует её
 * тем же кодом, что редактор.
 */
export const WAYFINDING_SIGN_ICON_PREFIX = 'wf-sign:';

/**
 * Шаг размера знака в id фигуры: близкие размеры делят одну картинку. До размера по
 * умолчанию (с запасом) размер в id не пишется — картинка базовая, id прежние.
 */
const SIGN_SHAPE_SIZE_STEP_PX = 8;
const SIGN_SHAPE_BASE_MAX_PX = 24;

/**
 * Фигура по умолчанию, залитая цветом типа. Цвет — hex (`readWayfindingSignTypes` проверил).
 *
 * `sizePx` — самый крупный размер знака на карте (у зум-выражения — наибольшая ступень):
 * фигура растровая, и картинка рисуется под него (✏️ 28.09 — крупные знаки мылились,
 * холст был один на все размеры). Входит в id с шагом {@link SIGN_SHAPE_SIZE_STEP_PX}.
 */
export function wayfindingSignShapeIconId(
  shape: WayfindingSignShape,
  color: string,
  sizePx?: number,
): string {
  const id = `${WAYFINDING_SIGN_ICON_PREFIX}shape:${shape}:${color}`;
  if (sizePx == null || !Number.isFinite(sizePx)) {
    return id;
  }
  const bucket = Math.ceil(sizePx / SIGN_SHAPE_SIZE_STEP_PX) * SIGN_SHAPE_SIZE_STEP_PX;
  return bucket > SIGN_SHAPE_BASE_MAX_PX ? `${id}@${bucket}` : id;
}

/** Наибольший размер знака: число как есть, у зум-выражения — наибольшая ступень. */
export function maxWayfindingMapSize(size: WayfindingMapSize): number {
  if (typeof size === 'number') {
    return size;
  }
  const first = size[0] === 'interpolate' ? 4 : size[0] === 'step' ? 2 : -1;
  let max = 0;
  if (first >= 0) {
    for (let i = first; i < size.length; i += 2) {
      const value = size[i];
      if (typeof value === 'number' && Number.isFinite(value)) {
        max = Math.max(max, value);
      }
    }
  }
  return max > 0 ? max : WAYFINDING_SIGN_MARKER_PX;
}

/** Пары «зум → размер» зум-выражения размера: у `interpolate` и `step` — с индекса 3. */
function wayfindingMapSizeStops(size: readonly unknown[]): Array<[number, number]> {
  const stops: Array<[number, number]> = [];
  for (let i = 3; i + 1 < size.length; i += 2) {
    stops.push([Number(size[i]), Number(size[i + 1])]);
  }
  return stops;
}

/** Зумы ступеней размера (у `step` — и точка перед ступенью, где ещё прежний размер); у числа их нет. */
function wayfindingMapSizeZooms(size: WayfindingMapSize): number[] {
  if (typeof size === 'number') {
    return [];
  }
  return wayfindingMapSizeStops(size).flatMap(([zoom]) => (size[0] === 'step' ? [zoom - 0.01, zoom] : [zoom]));
}

/**
 * Размер знака на этом зуме — как его посчитает MapLibre: число как есть, `step` — по ступеням,
 * `interpolate` — линейно или экспоненциально между ступенями, за краями — крайние значения.
 * `cubic-bezier` считается линейно: для оценок (налезание на плане целиком) точности хватает.
 */
export function wayfindingMapSizeAtZoom(size: WayfindingMapSize, zoom: number): number {
  if (typeof size === 'number') {
    return size;
  }
  const stops = wayfindingMapSizeStops(size);
  if (size[0] === 'step') {
    let value = Number(size[2]);
    for (const [stopZoom, stopValue] of stops) {
      if (zoom >= stopZoom) {
        value = stopValue;
      }
    }
    return value;
  }
  // Без `.at()`: shared едет и в публичную карту, а та живёт на iOS 15 (`Array#at` — с 15.4).
  const first = stops[0];
  const last = stops[stops.length - 1];
  if (!first || !last) {
    return 0;
  }
  if (zoom <= first[0]) {
    return first[1];
  }
  if (zoom >= last[0]) {
    return last[1];
  }
  const upper = stops.findIndex(([stopZoom]) => stopZoom >= zoom);
  const [z0, v0] = stops[upper - 1]!;
  const [z1, v1] = stops[upper]!;
  const interpolation = size[1];
  const base = Array.isArray(interpolation) && interpolation[0] === 'exponential' ? Number(interpolation[1]) : 1;
  const t = base === 1 ? (zoom - z0) / (z1 - z0) : (base ** (zoom - z0) - 1) / (base ** (z1 - z0) - 1);
  return v0 + (v1 - v0) * t;
}

/**
 * Эмодзи типа — своей картинкой, а не штатной `emoji:*`. Штатная нарисована крупной
 * (под места креатива, ~46 px), а все иконки одного слоя масштабируются одним
 * `icon-size`: рядом с фигурами в 16 px эмодзи вышел бы втрое больше.
 */
export function wayfindingSignEmojiIconId(emoji: string): string {
  return `${WAYFINDING_SIGN_ICON_PREFIX}emoji:${emoji}`;
}

export type WayfindingSignIcon =
  | {
      kind: 'shape';
      shape: WayfindingSignShape;
      color: string;
      /** Размер знака, под который рисовать картинку; нет — базовая. */
      sizePx?: number;
    }
  | { kind: 'emoji'; emoji: string };

const SIGN_SHAPES: ReadonlySet<string> = new Set(['circle', 'rect', 'rect-double', 'triangle', 'square']);

export function parseWayfindingSignIconId(id: string): WayfindingSignIcon | null {
  if (!id.startsWith(WAYFINDING_SIGN_ICON_PREFIX)) {
    return null;
  }
  const rest = id.slice(WAYFINDING_SIGN_ICON_PREFIX.length);
  if (rest.startsWith('emoji:')) {
    const emoji = rest.slice('emoji:'.length);
    return emoji ? { kind: 'emoji', emoji } : null;
  }
  if (rest.startsWith('shape:')) {
    const [shape, colorAndSize] = rest.slice('shape:'.length).split(':');
    const [color, size] = (colorAndSize ?? '').split('@');
    const sizePx = size ? Number(size) : undefined;
    if (
      shape &&
      SIGN_SHAPES.has(shape) &&
      color &&
      HEX_COLOR.test(color) &&
      (sizePx === undefined || (Number.isInteger(sizePx) && sizePx > 0))
    ) {
      return {
        kind: 'shape',
        shape: shape as WayfindingSignShape,
        color,
        ...(sizePx !== undefined ? { sizePx } : {}),
      };
    }
  }
  return null;
}

/**
 * Встроенная иконка типа — одна из фигур, выбранная явно (`icon: 'shape:triangle'`), а не
 * по числу сторон. Цвет в значение не входит: фигура всегда красится цветом типа.
 */
export const WAYFINDING_SIGN_BUILTIN_ICON_PREFIX = 'shape:';

export function wayfindingSignBuiltinIcon(shape: WayfindingSignShape): string {
  return `${WAYFINDING_SIGN_BUILTIN_ICON_PREFIX}${shape}`;
}

/** Фигура из значения иконки типа; `null` — иконка не встроенная (своя картинка) или пустая. */
export function wayfindingSignBuiltinShape(icon: string | undefined | null): WayfindingSignShape | null {
  if (!icon?.startsWith(WAYFINDING_SIGN_BUILTIN_ICON_PREFIX)) {
    return null;
  }
  const shape = icon.slice(WAYFINDING_SIGN_BUILTIN_ICON_PREFIX.length);
  return SIGN_SHAPES.has(shape) ? (shape as WayfindingSignShape) : null;
}

/**
 * Фигура знака типа: выбранная явно, иначе по числу сторон. У типа со своей картинкой —
 * тоже по сторонам: её берёт выгрузка, которая картинки не рисует.
 */
export function wayfindingSignTypeShape(
  type: Pick<WayfindingSignType, 'icon' | 'sides'>,
): WayfindingSignShape {
  return wayfindingSignBuiltinShape(type.icon) ?? wayfindingSignShape(type.sides);
}

/**
 * Иконка маркера типа на карте: своя картинка типа, иначе фигура (выбранная или по числу
 * сторон), залитая цветом типа.
 */
export function wayfindingSignMarkerIconId(
  type: Pick<WayfindingSignType, 'icon' | 'sides' | 'color'>,
  /** Самый крупный размер знака на карте — под него рисуется фигура. */
  sizePx?: number,
): string {
  const icon = type.icon;
  if (icon && !wayfindingSignBuiltinShape(icon)) {
    // Эмодзи и спрайты пикер типу больше не предлагает, но записанные раньше значения
    // рисуем как были, а не теряем.
    return icon.startsWith('emoji:') ? wayfindingSignEmojiIconId(icon.slice('emoji:'.length)) : icon;
  }
  return wayfindingSignShapeIconId(wayfindingSignTypeShape(type), type.color, sizePx);
}

/** Стабильный id блока отрисовки по типу — по нему его узнаёт синк карты. */
export const SIGN_TYPE_MARKER_VIZ_ID = 'viz-sign-type-marker';

/**
 * Размер маркера на карте по умолчанию, px. Иконки маркеров нарисованы в базе 16 px
 * (`signMarkerIcons.ts`). Свой размер задаёт карта — `settings.navigation.signSize`.
 */
export const WAYFINDING_SIGN_MARKER_PX = 22;

/** Диаметр точки объекта по умолчанию, px: внутри помещается иконка типа помещения. */
export const WAYFINDING_OBJECT_MARKER_PX = 20;

/** Размер подписи указателя по умолчанию, px: мельче штатной — названия длинные. */
export const WAYFINDING_SIGN_LABEL_PX = 12;

/** Размер подписи объекта по умолчанию, px — штатный размер подписи точки. */
export const WAYFINDING_OBJECT_LABEL_PX = 14;

/** Потолок размера знака: крупнее на плане не бывает, а мусор из jsonb раздул бы слой. */
const WAYFINDING_MAP_SIZE_MAX_PX = 200;

export interface WayfindingMapSizes {
  /** Знак указателя; превью макетов — пропорционально ему. */
  signSize: WayfindingMapSize;
  /** Точка объекта (диаметр). */
  objectSize: WayfindingMapSize;
  /** Подпись указателя (`text-size`). */
  signLabelSize: WayfindingMapSize;
  /** Подпись объекта (`text-size`). */
  objectLabelSize: WayfindingMapSize;
}

/**
 * Размер знака из свободного jsonb: число в пределах или зум-выражение, которое примет
 * MapLibre. Остальное — умолчание: битое выражение уронило бы слой целиком.
 */
export function readWayfindingMapSize(value: unknown, fallback: WayfindingMapSize): WayfindingMapSize {
  if (isFiniteNumber(value) && value > 0 && value <= WAYFINDING_MAP_SIZE_MAX_PX) {
    return value;
  }
  if (Array.isArray(value) && isWayfindingSizeZoomExpression(value)) {
    return value;
  }
  return fallback;
}

/** Поддерево режима (`settings.navigation`) из свободного jsonb; не объект — пустой объект. */
export function readWayfindingNavigation(settings: unknown): Record<string, unknown> {
  return isSignRecord(settings) && isSignRecord(settings.navigation) ? settings.navigation : {};
}

/**
 * Размеры знаков на карте (docs/wayfinding/plan.md §9.1.5): общие на карту — умолчание для
 * всех слоёв, чтобы указатели и объекты одного плана были одного масштаба. Слой может задать
 * свой (`readWayfindingLayerMarkerSize`).
 */
export function readWayfindingMapSizes(settings: unknown): WayfindingMapSizes {
  const navigation = readWayfindingNavigation(settings);
  return {
    signSize: readWayfindingMapSize(navigation.signSize, WAYFINDING_SIGN_MARKER_PX),
    objectSize: readWayfindingMapSize(navigation.objectSize, WAYFINDING_OBJECT_MARKER_PX),
    signLabelSize: readWayfindingMapSize(navigation.signLabelSize, WAYFINDING_SIGN_LABEL_PX),
    objectLabelSize: readWayfindingMapSize(navigation.objectLabelSize, WAYFINDING_OBJECT_LABEL_PX),
  };
}

/**
 * Свой размер знаков слоя поверх общего размера карты (✏️ 23.09, Паша: «мало ли кому-то надо
 * разные слои настроить по-разному»). У слоя указателей — размер знака, у слоя «Объекты» —
 * диаметр точки. Ключ в корне `styleConfig`: у указателей и объектов блоки разные, а размер
 * у слоя один. Нет ключа — размер карты (`readWayfindingMapSizes`).
 */
export const WAYFINDING_LAYER_MARKER_SIZE_KEY = 'wayfindingMarkerSize';

/** Размер знаков слоя: свой, если задан и годен, иначе размер карты `projectSize`. */
export function readWayfindingLayerMarkerSize(
  styleConfig: Record<string, unknown> | null | undefined,
  projectSize: WayfindingMapSize,
): WayfindingMapSize {
  const own = styleConfig?.[WAYFINDING_LAYER_MARKER_SIZE_KEY];
  return own == null ? projectSize : readWayfindingMapSize(own, projectSize);
}

/**
 * Размер, умноженный на коэффициент: у числа — само число, у зум-выражения — каждая ступень
 * (зумы не трогаем). Так превью следуют за знаком и на карте с размером «по зуму».
 */
export function scaleWayfindingMapSize(size: WayfindingMapSize, factor: number): WayfindingMapSize {
  const round = (value: number) => Math.round(value * factor * 100) / 100;
  if (typeof size === 'number') {
    return round(size);
  }
  // `interpolate`: [оп, интерполяция, вход, зум, размер, …] — размеры с индекса 4 через один;
  // `step`: [оп, вход, размер, зум, размер, …] — с индекса 2.
  const firstValue = size[0] === 'step' ? 2 : 4;
  return size.map((item, index) =>
    index >= firstValue && (index - firstValue) % 2 === 0 && isFiniteNumber(item) ? round(item) : item,
  );
}

const ALL_GEOMETRIES: ReadonlySet<GeometryCategory> = new Set(['point', 'line', 'polygon']);

/**
 * Подпись слоя указателей по умолчанию — НАЗВАНИЕ указателя (docs/wayfinding/plan.md
 * §9.1.2): `showLabels` не задан — подписи включены, `labelProperty` не задан — читается
 * вычисленное название (`WAYFINDING_SIGN_NAME_LABEL_PROPERTY`, его подкладывают в данные
 * карты). Явно выключенные подписи и явно выбранное поле уважаются.
 *
 * Правило чтения, как и сам стиль: слой без этих ключей в базе остаётся без них. Панель
 * слоя показывает те же умолчания — зовёт эту же функцию.
 */
export function withWayfindingSignLabelDefaults(
  styleConfig: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const base = styleConfig ?? {};
  const labelProperty =
    typeof base.labelProperty === 'string' && base.labelProperty !== ''
      ? base.labelProperty
      : WAYFINDING_SIGN_NAME_LABEL_PROPERTY;
  return {
    ...base,
    showLabels: typeof base.showLabels === 'boolean' ? base.showLabels : true,
    labelProperty,
  };
}

/** Блоки, которые рисуют подписи, — их текстовые стили правит блок «Подписи» панели слоя. */
const LABEL_CAPABLE_KINDS: ReadonlySet<string> = new Set([
  'circle',
  'symbol',
  'preview-point',
  'fill',
  'sign',
]);

/**
 * Только текстовые ключи: цвет и форма точки к знаку типа не относятся. Шрифта тоже нет
 * (✏️ 13.09): указатели подписаны шрифтом карты, выбора шрифта у слоя указателей нет.
 */
function pickTextStyle(record: Record<string, unknown> | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record ?? {})) {
    if (key.startsWith('text-') && key !== 'text-field' && key !== 'text-font') {
      out[key] = value;
    }
  }
  return out;
}

/**
 * СТАРАЯ запись настроек превью макетов — ключ `styleConfig` слоя указателей (до 13.09).
 * Теперь настройки живут в блоке «Указатели» (`kind: 'sign'`); ключ читается только при сборке
 * блока из старой формы (`toWayfindingSignStyleConfig`) и уходит с первой правкой в панели.
 */
export const WAYFINDING_SIGN_PREVIEW_STYLE_KEY = 'signPreview';

/**
 * Стабильный id блока «Указатели», собранного из старой формы стиля: ключ карточки в панели и
 * кэши не прыгают от перечитывания к перечитыванию.
 */
export const WAYFINDING_SIGN_VIZ_ID = 'viz-signs';

/** Поля старой записи `signPreview`, которые переезжают в блок «Указатели». */
const LEGACY_SIGN_PREVIEW_KEYS = [
  'previewSize',
  'previewBorderWidth',
  'previewFreeBackground',
] as const;

function isSignRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Блок «Указатели» из старой формы стиля: текстовые стили подписи — у верхнего блока слоя,
 * умеющего подписи (их и правила секция «Подписи»), настройки превью — из `signPreview`.
 */
function legacyWayfindingSignBlock(
  base: Record<string, unknown>,
  resolved: readonly LayerVisualization[],
): LayerVisualization {
  const labelSource = [...orderVisualizationsForRender(resolved)]
    .reverse()
    .find((viz) => viz.enabled && LABEL_CAPABLE_KINDS.has(viz.kind));
  const stored = base[WAYFINDING_SIGN_PREVIEW_STYLE_KEY];
  const legacy = isSignRecord(stored) ? stored : {};
  const legacyPaint = isSignRecord(legacy.paint) ? legacy.paint : {};
  const block: Record<string, unknown> = {
    id: WAYFINDING_SIGN_VIZ_ID,
    kind: 'sign',
    enabled: true,
    paint: {
      ...pickTextStyle(labelSource?.paint),
      ...(legacyPaint['circle-stroke-color'] !== undefined
        ? { 'circle-stroke-color': legacyPaint['circle-stroke-color'] }
        : {}),
    },
    layout: pickTextStyle(labelSource?.layout),
  };
  if (legacy.enabled === false) {
    block.previewsEnabled = false;
  }
  for (const key of LEGACY_SIGN_PREVIEW_KEYS) {
    if (legacy[key] !== undefined) {
      block[key] = legacy[key];
    }
  }
  return block as unknown as LayerVisualization;
}

/**
 * Стиль слоя указателей в форме с блоком «Указатели» (`kind: 'sign'`, ✏️ 13.09): точку слоя
 * рисует ОН — знак типа, превью макетов и подпись, — а линии и полигоны остаются своими
 * блоками. Точечные блоки другого вида отбрасываются: у указателей карта их не рисует.
 *
 * Старая форма (точечные блоки и ключ `signPreview`) приводится к новой на лету: слои без
 * блока не проходят миграцию jsonb, а первая правка в панели сохраняет уже новую форму.
 * Приведённый стиль возвращается тем же объектом — кэши по ссылке не сбрасываются.
 *
 * @param options.geometries — геометрии данных слоя: старой generic-форме, чтобы не выдумывать
 *   блоки линий и полигонов, которых в слое нет (панель). Нет — все, как рисует карта.
 */
export function toWayfindingSignStyleConfig(
  styleConfig: Record<string, unknown> | null | undefined,
  options?: { geometries?: ReadonlySet<GeometryCategory> },
): Record<string, unknown> {
  const base = styleConfig ?? {};
  const stored = Array.isArray(base.visualizations) ? (base.visualizations as unknown[]) : null;
  if (
    stored &&
    !(WAYFINDING_SIGN_PREVIEW_STYLE_KEY in base) &&
    stored.filter((viz) => isSignRecord(viz) && viz.kind === 'sign').length === 1 &&
    stored.every(
      (viz) =>
        isSignRecord(viz) &&
        typeof viz.kind === 'string' &&
        (viz.kind === 'sign' || visualizationKindGeometry(viz.kind as VisualizationKind) !== 'point'),
    )
  ) {
    return base;
  }
  const resolved = resolveVisualizations(base as unknown as LayerStyleConfig, {
    geometries: options?.geometries ?? ALL_GEOMETRIES,
  }).filter((viz) => isSignRecord(viz) && typeof viz.kind === 'string');
  const sign =
    resolved.find((viz) => viz.kind === 'sign') ?? legacyWayfindingSignBlock(base, resolved);
  const kept = resolved.filter(
    (viz) => viz.kind !== 'sign' && visualizationKindGeometry(viz.kind) !== 'point',
  );
  const next: Record<string, unknown> = { ...base, visualizations: [...kept, sign] };
  delete next[WAYFINDING_SIGN_PREVIEW_STYLE_KEY];
  return next;
}

/** Блок «Указатели» слоя указателей: настройки знака, превью макетов и подписи. */
export function readWayfindingSignVisualization(
  styleConfig: Record<string, unknown> | null | undefined,
): LayerVisualization {
  const blocks = toWayfindingSignStyleConfig(styleConfig).visualizations as LayerVisualization[];
  return blocks.find((viz) => viz.kind === 'sign') ?? legacyWayfindingSignBlock({}, []);
}

/** Стабильный id блока превью макетов — по нему его узнаёт синк карты. */
export const SIGN_PREVIEW_VIZ_ID = 'viz-sign-previews';

export type WayfindingSignPreviewShape = 'circle' | 'rounded-square' | 'pin' | 'free';

export interface WayfindingSignPreviewStyle {
  enabled: boolean;
  previewShape: WayfindingSignPreviewShape;
  /** Меньшая сторона превью в px или зум-выражение — как `previewSize` блока. */
  previewSize: number | unknown[];
  previewBorderWidth: number;
  /** Фон под макетом — обязателен (✏️ 13.09): макет на плане без подложки теряется. */
  previewFreeBackground: string;
  borderColor: string;
}

/**
 * Якорь превью по умолчанию: ряд справа от знака. Не слева и не снизу — слева и снизу
 * у знака подпись и место под неё. Свой якорь задаёт ТИП указателя
 * (`WayfindingSignType.previewAnchor`); слой (`WAYFINDING_LAYER_PREVIEW_ANCHOR_KEY`) и отдельный
 * указатель (`WAYFINDING_SIGN_PREVIEW_ANCHOR_PROPERTY`) могут переопределить его для себя.
 */
export const WAYFINDING_SIGN_PREVIEW_DEFAULT_ANCHOR = 'left';

/**
 * Умолчания: макет целиком (свободная форма) на белом, справа от точки — слева от неё
 * стоит сам знак, а снизу подпись. Размер мельче, чем у точек Автокрафта: на плане
 * указателей десятки, и у каждого до четырёх превью в ряд.
 */
export const WAYFINDING_SIGN_PREVIEW_DEFAULTS: Readonly<Omit<WayfindingSignPreviewStyle, 'enabled'>> = {
  previewShape: 'free',
  previewSize: 40,
  previewBorderWidth: 1,
  previewFreeBackground: '#ffffff',
  borderColor: '#a1a1aa',
};


/** Допустимые значения MapLibre `icon-anchor`. */
export const WAYFINDING_ICON_ANCHORS: readonly string[] = [
  'center', 'left', 'right', 'top', 'bottom',
  'top-left', 'top-right', 'bottom-left', 'bottom-right',
];
const ICON_ANCHORS: ReadonlySet<string> = new Set(WAYFINDING_ICON_ANCHORS);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * Зум-выражение размера в том виде, какой примет MapLibre: `interpolate`/`step` по `["zoom"]`,
 * хотя бы одна пара «зум → размер», числа и строго растущие зумы. Синк карты подставляет
 * массив в `icon-size`, лишь поделив числа, — битое выражение уронило бы слой превью целиком.
 */
export function isWayfindingSizeZoomExpression(value: readonly unknown[]): boolean {
  const isZoomInput = (input: unknown) =>
    Array.isArray(input) && input.length === 1 && input[0] === 'zoom';
  // `[оператор, интерполяция|вход, вход|размер, зум, размер, …]` — пары после третьего элемента.
  if (value.length < 5 || value.length % 2 === 0) {
    return false;
  }
  if (value[0] === 'interpolate') {
    const interpolation = value[1];
    const knownInterpolation =
      Array.isArray(interpolation) &&
      ((interpolation.length === 1 && interpolation[0] === 'linear') ||
        (interpolation.length === 2 &&
          interpolation[0] === 'exponential' &&
          isFiniteNumber(interpolation[1])) ||
        (interpolation.length === 5 &&
          interpolation[0] === 'cubic-bezier' &&
          interpolation.slice(1).every(isFiniteNumber)));
    if (!knownInterpolation || !isZoomInput(value[2])) {
      return false;
    }
  } else if (value[0] === 'step') {
    if (!isZoomInput(value[1]) || !isFiniteNumber(value[2]) || value[2] < 0) {
      return false;
    }
  } else {
    return false;
  }
  let previousZoom = -Infinity;
  for (let index = 3; index < value.length; index += 2) {
    const zoom = value[index];
    const size = value[index + 1];
    if (!isFiniteNumber(zoom) || !isFiniteNumber(size) || size < 0 || zoom <= previousZoom) {
      return false;
    }
    previousZoom = zoom;
  }
  return true;
}

/**
 * Настройки превью из блока «Указатели» (у старой формы — из блока, собранного на лету). Стиль
 * слоя — свободный jsonb: битое и незнакомое заменяется умолчанием. ✏️ 13.09 форма всегда
 * свободная, фон обязателен — снятый в старой записи фон читается умолчанием.
 *
 * @param options.defaultBorderColor — обводка, пока своего цвета нет (цвет типа носителя слоя).
 */
export function readWayfindingSignPreviewStyle(
  styleConfig: Record<string, unknown> | null | undefined,
  options?: { defaultBorderColor?: string },
): WayfindingSignPreviewStyle {
  const entry = readWayfindingSignVisualization(styleConfig) as unknown as Record<string, unknown>;
  const defaults = WAYFINDING_SIGN_PREVIEW_DEFAULTS;
  const paint =
    entry.paint && typeof entry.paint === 'object' && !Array.isArray(entry.paint)
      ? (entry.paint as Record<string, unknown>)
      : {};
  const borderColor = paint['circle-stroke-color'];
  const size = entry.previewSize;
  const borderWidth = entry.previewBorderWidth;
  const background = entry.previewFreeBackground;
  return {
    enabled: entry.previewsEnabled !== false,
    // Превью макетов всегда свободной формы: макет показывается целиком.
    previewShape: defaults.previewShape,
    previewSize:
      (isFiniteNumber(size) && size > 0) || (Array.isArray(size) && isWayfindingSizeZoomExpression(size))
        ? size
        : defaults.previewSize,
    previewBorderWidth:
      typeof borderWidth === 'number' && Number.isFinite(borderWidth) && borderWidth >= 0
        ? borderWidth
        : defaults.previewBorderWidth,
    previewFreeBackground:
      typeof background === 'string' && background !== '' ? background : defaults.previewFreeBackground,
    borderColor:
      typeof borderColor === 'string' && borderColor !== ''
        ? borderColor
        : (options?.defaultBorderColor ?? defaults.borderColor),
  };
}

/**
 * Блок превью макетов для карты; `null` — превью выключены.
 *
 * Картинки берутся из вычисленного свойства точки (`WAYFINDING_SIGN_PREVIEWS_PROPERTY`,
 * по превью на сторону). Блок — «спутник» маркера (`previewBesideMarker`): подписей не
 * рисует (их рисует маркер), при переполнении карты пропадает, а не превращается в
 * кружки, у точки без макетов ничего не рисует и держится в стороне от знака.
 * Поворачивается вместе со знаком (`signOrientation`): картинка раскладки собрана вокруг
 * точки, и поворот вокруг её центра оставляет превью каждой стороны у своей грани.
 *
 * Якорь блока — всегда «по центру»: куда встаёт превью каждой стороны, решено уже в
 * ДАННЫХ карты (угол рядом с каждой ссылкой, `wayfindingSignPreviewEntries`) и запечено
 * в самой картинке. Выражением по типу это не сделать: у знака с двумя сторонами превью
 * уходят в РАЗНЫЕ стороны от одной точки, а `icon-anchor` у символа один.
 */
export function wayfindingSignPreviewBlock(
  styleConfig: Record<string, unknown> | null | undefined,
  signSize: WayfindingMapSize = WAYFINDING_SIGN_MARKER_PX,
  options?: { signTypes?: readonly WayfindingSignType[]; layerSignType?: WayfindingSignType | null },
): LayerVisualization | null {
  const style = readWayfindingSignPreviewStyle(styleConfig);
  /**
   * Своего цвета обводки у слоя нет — у каждого указателя цвет его типа (✏️ 13.09), тем же
   * механизмом, что «Раскраска по параметру»: у точки без своего типа — цвет типа слоя, у слоя
   * без типа — умолчание. Свой цвет у слоя — один на все указатели.
   */
  const storedBorder = readWayfindingSignVisualization(styleConfig).paint?.['circle-stroke-color'];
  const borderByType = !(typeof storedBorder === 'string' && storedBorder !== '');
  const typeBorderColors: Record<string, string> = {};
  for (const type of options?.signTypes ?? []) {
    typeBorderColors[type.id] = type.color;
  }
  const fallbackBorderColor = options?.layerSignType?.color ?? style.borderColor;
  if (!style.enabled) {
    return null;
  }
  // Размер превью — от знака, в той же пропорции, что умолчания (✏️ 13.09): размер знаков
  // задаёт карта, и превью обязаны расти вместе со знаком, а не жить своим числом у слоя.
  const previewSize = scaleWayfindingMapSize(
    signSize,
    (WAYFINDING_SIGN_PREVIEW_DEFAULTS.previewSize as number) / WAYFINDING_SIGN_MARKER_PX,
  );
  return {
    id: SIGN_PREVIEW_VIZ_ID,
    kind: 'preview-point',
    enabled: true,
    paint: { 'circle-stroke-color': borderByType ? fallbackBorderColor : style.borderColor },
    layout: {},
    ...(borderByType && Object.keys(typeBorderColors).length > 0
      ? {
          colorByProperty: WAYFINDING_SIGN_TYPE_PROPERTY,
          colorMapping: typeBorderColors,
          colorFallback: fallbackBorderColor,
        }
      : {}),
    previewParamName: WAYFINDING_SIGN_PREVIEWS_PROPERTY,
    previewShape: style.previewShape,
    // Размер по площади: квадратный макет иначе ужат до меньшей стороны 40 px и нечитаем,
    // а вытянутый получает вчетверо больше места (docs/map-view/preview-quality.md).
    previewFit: 'area',
    previewSize,
    previewBorderWidth: style.previewBorderWidth,
    previewFreeBackground: style.previewFreeBackground,
    previewAnchor: 'center',
    suppressText: true,
    previewBesideMarker: true,
    showSymbolRotation: true,
    symbolRotationAngleProperty: WAYFINDING_SIGN_ORIENTATION_PROPERTY,
  };
}

/**
 * Якорь подписи знака для карты — `text-anchor` по данным: свой якорь указателя, иначе свой у
 * слоя, иначе якорь типа (свой тип точки, а незнакомый и отсутствующий — тип слоя, как у знака),
 * иначе умолчание. Своё значение точки проходит, только если это известное положение.
 */
export function wayfindingSignLabelAnchorExpression(
  signTypes: readonly Pick<WayfindingSignType, 'id' | 'labelAnchor'>[],
  layerType: Pick<WayfindingSignType, 'labelAnchor'> | null | undefined,
  layerAnchor: string | null | undefined,
): unknown[] {
  const fallback = resolveWayfindingInheritedLabelAnchor(layerAnchor, layerType);
  const byType = layerAnchor
    ? []
    : signTypes.flatMap((type) => {
        const anchor = resolveWayfindingInheritedLabelAnchor(null, type);
        return anchor === fallback ? [] : [type.id, anchor];
      });
  return [
    'match',
    ['get', WAYFINDING_SIGN_LABEL_ANCHOR_PROPERTY],
    [...WAYFINDING_ICON_ANCHORS],
    ['get', WAYFINDING_SIGN_LABEL_ANCHOR_PROPERTY],
    byType.length > 0 ? ['match', ['get', WAYFINDING_SIGN_TYPE_PROPERTY], ...byType, fallback] : fallback,
  ];
}

/**
 * Якорь подписи одного указателя — та же цепочка, что `wayfindingSignLabelAnchorExpression`, для
 * тех, кто рисует без MapLibre (лист выгрузки): свой у точки, иначе слоя, иначе типа (свой тип
 * точки, а незнакомый — тип слоя), иначе умолчание.
 */
export function resolveWayfindingSignLabelAnchor(
  properties: Record<string, unknown>,
  layerAnchor: unknown,
  layerSignTypeId: string | null,
  typeById: ReadonlyMap<string, Pick<WayfindingSignType, 'labelAnchor'>>,
): string {
  const own = readWayfindingSignLabelAnchor(properties);
  if (own) {
    return own;
  }
  const typeId = resolveWayfindingSignTypeId(properties, layerSignTypeId, typeById);
  return resolveWayfindingInheritedLabelAnchor(readIconAnchor(layerAnchor), typeId ? typeById.get(typeId) : null);
}

/**
 * Ключи размещения подписи, которые у знака указателя задаёт якорь подписи
 * (`withSignLabelPlacement`), — панель слоя их не показывает.
 */
export const WAYFINDING_SIGN_LABEL_PLACEMENT_KEYS: readonly string[] = [
  'text-anchor',
  'text-offset',
  'text-radial-offset',
  'text-variable-anchor',
  'text-variable-anchor-offset',
  'text-justify',
];

/** Зазор между краем знака и подписью, px: при умолчаниях (22 и 12 px) — прежние 1.1 em. */
const SIGN_LABEL_GAP_PX = 2.2;

/** Зум, на котором берётся размер знака «по зуму» для листа выгрузки: план читают крупно. */
const SIGN_LABEL_REFERENCE_ZOOM = 18;

/**
 * Отступ подписи от центра знака в половинах знака — для листа выгрузки, где знак не в пикселях:
 * половина знака и тот же зазор, что на карте при размере знака `signSize` (у размера по зуму —
 * на крупном зуме). Крупный знак — зазор меньше в его долях, как и на карте.
 */
export function wayfindingSignLabelOffsetHalves(signSize: WayfindingMapSize = WAYFINDING_SIGN_MARKER_PX): number {
  const px = wayfindingMapSizeAtZoom(signSize, SIGN_LABEL_REFERENCE_ZOOM);
  return 1 + SIGN_LABEL_GAP_PX / Math.max(1, px / 2);
}

/**
 * Поправка MapLibre на базовую линию: у якорей сверху и снизу он вычитает из радиального отступа
 * 7/24 em (`variable_text_anchor.ts`). Возвращаем её — иначе подпись под знаком встала бы ближе
 * прежнего `text-offset`, а сбоку от знака зазор просто чуть шире.
 */
const MAPLIBRE_BASELINE_OFFSET_EM = 7 / 24;

/**
 * Layout подписи знака с размещением по якорю подписи. Отступ от точки — в сторону якоря
 * (`text-radial-offset`): половина знака и зазор в долях кегля. У размеров «по зуму» — тоже по
 * зуму, по ступеням обоих (между ними линейно: layout MapLibre всё равно считает на целых зумах),
 * иначе мелкая подпись при крупной на другом зуме залезла бы на знак. Переменный якорь стиля
 * слоя снимается — он главнее `text-anchor`. `text-offset` — явный ноль: снятый ключ синк
 * дополнил бы умолчанием символов (`[0, 0.5]`). Строки — к знаку.
 */
function withSignLabelPlacement(
  layout: unknown,
  anchor: unknown[],
  signSize: WayfindingMapSize,
  labelSize: WayfindingMapSize,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...(isSignRecord(layout) ? layout : {}) };
  delete next['text-variable-anchor'];
  delete next['text-variable-anchor-offset'];
  const textSize = readWayfindingMapSize(next['text-size'], labelSize);
  const offsetAt = (zoom: number) => {
    const textPx = Math.max(1, wayfindingMapSizeAtZoom(textSize, zoom));
    const em = (wayfindingMapSizeAtZoom(signSize, zoom) / 2 + SIGN_LABEL_GAP_PX) / textPx;
    return Math.round((em + MAPLIBRE_BASELINE_OFFSET_EM) * 100) / 100;
  };
  const zooms = [...new Set([...wayfindingMapSizeZooms(signSize), ...wayfindingMapSizeZooms(textSize)])].sort(
    (a, b) => a - b,
  );
  return {
    ...next,
    'text-anchor': anchor,
    'text-radial-offset':
      zooms.length < 2
        ? offsetAt(zooms[0] ?? 0)
        : ['interpolate', ['linear'], ['zoom'], ...zooms.flatMap((zoom) => [zoom, offsetAt(zoom)])],
    'text-offset': [0, 0],
    'text-justify': 'auto',
  };
}

/** Свойства точки, которые читают выражения знака указателя, — без них в тайлах знак не тот. */
export const WAYFINDING_SIGN_MAP_PROPERTIES: readonly string[] = [
  WAYFINDING_SIGN_TYPE_PROPERTY,
  WAYFINDING_SIGN_ORIENTATION_PROPERTY,
  WAYFINDING_SIGN_LABEL_ANCHOR_PROPERTY,
];

/**
 * Стиль слоя указателей для КАРТЫ: точка рисуется маркером своего типа — своей иконкой
 * типа или фигурой по числу сторон, залитой цветом типа — и подписана названием
 * указателя (✏️ 11.09). Рядом со знаком — превью макетов его сторон (блок превью, см.
 * `wayfindingSignPreviewBlock`). Всё это — раскрытый блок «Указатели» слоя (✏️ 13.09,
 * `toWayfindingSignStyleConfig`): до синка карты он не доходит, а скрытый блок не рисует ни
 * знака, ни превью. Линии и полигоны остаются как настроены.
 *
 * Это правило ЧТЕНИЯ, в `styleConfig` слоя ничего не пишется: иначе цвет и иконка жили
 * бы в двух местах (справочник и каждый слой указателей), и любая правка типа обязана
 * была бы переписывать все слои — а путей создания слоя десяток. Зовут его редактор и
 * вьювер ровно там, где слои уходят в карту.
 *
 * Один symbol-блок с иконкой по параметру `signType` — тот же механизм, что «символ по
 * параметру» в настройках слоя. Параметр — свой тип указателя (переопределение); точка без
 * своего типа берёт фолбэк блока — знак типа слоя (✏️ 13.09), у слоя без типа — серый круг.
 *
 * Маркер стоит в массиве блоков раньше превью: главный точечный блок слоя — первый
 * по порядку, по нему ищутся угол поворота и ручка поворота. Превью при этом рисуется
 * поверх знака, но в стороне от него.
 *
 * Текстовые стили подписи (размер, цвет, обводка) — у блока «Указатели»: их правит секция
 * «Подписи» панели слоя. Место подписи у знака — якорь подписи (`withSignLabelPlacement`).
 *
 * @param options.previews — `false` у тайлового слоя: вычисленного свойства с превью в
 *   тайлах нет, и блок нарисовал бы у каждой точки серую заглушку.
 * @param options.signSize — размер знака из настроек карты (`readWayfindingMapSizes`); свой
 *   размер слоя (`readWayfindingLayerMarkerSize`) важнее. Превью макетов растут пропорционально.
 * @param options.layerSignTypeId — тип носителя слоя (`readWayfindingLayerSignTypeId`): его знак
 *   у точек без своего типа.
 * @param options.labelSize — размер подписи из настроек карты; свой размер подписи слоя важнее.
 */
export function applySignTypeStyle(
  styleConfig: Record<string, unknown> | null | undefined,
  signTypes: readonly WayfindingSignType[],
  options?: {
    previews?: boolean;
    signSize?: WayfindingMapSize;
    layerSignTypeId?: string | null;
    labelSize?: WayfindingMapSize;
  },
): Record<string, unknown> {
  const signSize = readWayfindingLayerMarkerSize(
    styleConfig,
    options?.signSize ?? WAYFINDING_SIGN_MARKER_PX,
  );
  const layerSignTypeId = options?.layerSignTypeId;
  const layerType = layerSignTypeId
    ? signTypes.find((type) => type.id === layerSignTypeId)
    : undefined;
  // Размер подписи карты — умолчание: свой размер подписи слоя (блок или `labelStyle`) важнее.
  const labelSize = options?.labelSize ?? WAYFINDING_SIGN_LABEL_PX;
  const labelDefaults = { 'text-size': labelSize };
  // Старую форму приведение разворачивает со всеми геометриями: generic-тип рисовал и линии
  // с полигонами, и без этого они пропали бы вместе с точечным блоком.
  const base = toWayfindingSignStyleConfig(withWayfindingSignLabelDefaults(styleConfig));
  const blocks = base.visualizations as LayerVisualization[];
  const sign = blocks.find((viz) => viz.kind === 'sign');
  const kept = blocks.filter((viz) => viz.kind !== 'sign');
  if (!sign?.enabled) {
    return withLabelLayoutDefaults({ ...base, visualizations: kept }, labelDefaults);
  }

  // Подпись читает явный `labelStyle` слоя, а без него — ключи блока (`resolveLabelSubLayerStyle`):
  // размещение пишется в оба, отступ — от кегля того места, откуда его прочтут.
  const labelStyle = isSignRecord(base.labelStyle) ? base.labelStyle : null;
  const anchor = wayfindingSignLabelAnchorExpression(signTypes, layerType, readWayfindingLayerLabelAnchor(styleConfig));
  const placeLabel = (layout: unknown) => withSignLabelPlacement(layout, anchor, signSize, labelSize);

  // Фигура растровая — рисуется под самый крупный размер знака слоя.
  const markerPx = maxWayfindingMapSize(signSize);
  const iconMapping: Record<string, string> = {};
  for (const type of signTypes) {
    iconMapping[type.id] = wayfindingSignMarkerIconId(type, markerPx);
  }

  const marker: LayerVisualization = {
    id: SIGN_TYPE_MARKER_VIZ_ID,
    kind: 'symbol',
    enabled: true,
    paint: {
      // Обводка плотнее штатной: подпись ложится на чертёж, а не на спокойную подложку.
      'text-halo-width': 1.5,
      ...pickTextStyle(sign.paint),
    },
    layout: placeLabel({
      // Фолбэк — точкам без своего типа: знак типа слоя, у слоя без типа — серый круг.
      'icon-image': layerType
        ? wayfindingSignMarkerIconId(layerType, markerPx)
        : wayfindingSignShapeIconId('circle', WAYFINDING_SIGN_UNTYPED_HEX, markerPx),
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      // Подпись, которой не хватило места, прячется одна: знак указателя остаётся.
      'text-optional': true,
      ...pickTextStyle(sign.layout),
    }),
    iconSizePx: signSize,
    // Знак повёрнут по углу указателя; тот же флаг включает у выбранной точки ручку
    // поворота — механика точек Автокрафта в редакторе. Свойство угла в схему слоя
    // добавляет `applySignTypeSource`.
    showSymbolRotation: true,
    symbolRotationAngleProperty: WAYFINDING_SIGN_ORIENTATION_PROPERTY,
    // Название — поверх превью макетов вокруг знака, в том числе крупных копий наведённого
    // и выбранного.
    labelsOnTop: true,
    // Пустой справочник: без записи в mapping синк не строит `match`, и все точки берут фолбэк.
    ...(Object.keys(iconMapping).length > 0
      ? { iconByProperty: WAYFINDING_SIGN_TYPE_PROPERTY, iconMapping }
      : {}),
  };
  const previews = options?.previews === false ? null : wayfindingSignPreviewBlock(base, signSize, { signTypes, layerSignType: layerType });
  return withLabelLayoutDefaults(
    {
      ...base,
      visualizations: [...kept, marker, ...(previews ? [previews] : [])],
      ...(labelStyle ? { labelStyle: { ...labelStyle, layout: placeLabel(labelStyle.layout) } } : {}),
    },
    labelDefaults,
  );
}

/**
 * Схема слоя указателей для КАРТЫ: свойство угла поворота как параметр типа `angle`.
 *
 * Поворот символа и ручка поворота ищут угол среди параметров слоя этого типа. Правило
 * чтения, как и стиль: в `sourceConfig` слоя ничего не пишется, иначе каждый путь создания
 * слоя указателей обязан был бы его добавить, а старые слои — пройти миграцию.
 */
export function applySignTypeSource(
  sourceConfig: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const base = sourceConfig ?? {};
  const defs = Array.isArray(base.customProperties)
    ? (base.customProperties as CustomPropertyDef[])
    : [];
  if (defs.some((def) => def?.name === WAYFINDING_SIGN_ORIENTATION_PROPERTY)) {
    return base;
  }
  return {
    ...base,
    customProperties: [
      ...defs,
      // Своё поле у указателя в панели «Навигации»; generic-форме объекта оно ни к чему.
      { name: WAYFINDING_SIGN_ORIENTATION_PROPERTY, type: 'angle', hiddenInEditor: true },
    ],
  };
}
